import { useCallback, useMemo, useEffect } from 'react'
import { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useFormikContext, useField } from 'formik'
import { useSelector } from 'react-redux'
import { ICampaign, selectAllCampaigns } from '@user/store/campaigns'

type Props = {
  required: boolean
  name: string
}

export function useData(props: Props) {
  const { name, required } = props
  const {
    setFieldValue,
    isSubmitting,
    values: { campaignId },
  } = useFormikContext<IAddOpportunityFormModel>()

  const [{}, { error, touched }] = useField<string>({
    name,
    validate: (value) => {
      if (required && !value) {
        return 'Required'
      }
    },
  })

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const campaigns = useSelector(selectAllCampaigns)

  const options = useMemo(
    () =>
      campaigns.filter(
        (row, index, list) =>
          list.findIndex((listRow) => listRow.id === row.id) === index,
      ),
    [campaigns],
  )

  const getLabel = useCallback(
    (option: ICampaign) =>
      option.description?.trim()
        ? `${option?.name} - ${option.description}`
        : option?.name,
    [],
  )

  const getId = useCallback((option: ICampaign) => {
    return String(option.id)
  }, [])

  useEffect(() => {
    if (!campaignId && required && options?.length == 1) {
      setFieldValue(name, options[0].id)
    }
  }, [campaignId, required, setFieldValue])

  const initialOption = useMemo(() => {
    if (campaignId) {
      const value = options.find((x) => x.id == campaignId)

      if (value) return value
    }
    return null
  }, [options, campaignId])

  return {
    getLabel,
    getId,
    items: options,
    initialOption,
    isSubmitting,
    showError,
    helperText,
  }
}
